import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import type { MenuItem } from '@/shared/types/common';
import { Svg } from '@/shared/ui/Svg';
import ChevronDownIcon from '@/shared/assets/icons/chevron-down.svg';
import { useNestedMenuOpen } from '../../lib/hooks/useNestedMenuOpen';
import { isPathMatching, normalizePath } from '../../lib/utils/navigation';
import styles from '../../ui/Sidebar/Sidebar.module.scss';

type NestedMenuItemProps = {
  item: MenuItem;
  pathname: string;
};

export const NestedMenuItem = ({ item, pathname }: NestedMenuItemProps) => {
  const navigate = useNavigate();
  const children = item.children ?? [];
  const isParentActive = item.href ? pathname === normalizePath(item.href) : false;

  const activeChild = children.find((child) => isPathMatching(child.href, pathname));
  const isChildActive = Boolean(activeChild);
  const { isOpen, toggle } = useNestedMenuOpen({ isChildActive });

  const shouldHighlightParent = isParentActive && !isChildActive;

  const handleChildClick = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigate(href);
  };

  return (
    // Пункт списка, а не div: родитель — <ul>, и он принимает только <li>.
    <li className="flex flex-col">
      <button
        type="button"
        onClick={toggle}
        className={cn(styles.listBoxItemBase, {
          ['bg-red']: shouldHighlightParent || isChildActive,
        })}
      >
        <div className="flex items-center gap-3 w-full">
          {item.icon && (
            <Svg
              Icon={item.icon}
              className={cn({ ['!text-white']: shouldHighlightParent || isChildActive })}
              width={24}
              height={24}
            />
          )}
          <span
            className={cn(styles.listBoxTitle, {
              ['!text-white']: shouldHighlightParent || isChildActive,
            })}
          >
            {item.label}
          </span>
          <div
            className={cn('ml-auto transition-transform', {
              'rotate-180': isOpen,
            })}
          >
            <Svg
              Icon={ChevronDownIcon}
              className={cn('text-black', { ['text-white']: isChildActive })}
            />
          </div>
        </div>
      </button>
      <div
        className={cn('overflow-hidden transition-all duration-300 ease-in-out', {
          'max-h-0': !isOpen,
          'max-h-[500px]': isOpen,
        })}
      >
        <div className="flex flex-col gap-2 mt-2 pl-8">
          {children.map((child) => {
            if (!child.href) return null;
            const childActive = isPathMatching(child.href, pathname);
            return (
              <Link
                key={child.id}
                to={child.href}
                onClick={handleChildClick(child.href)}
                className={cn(styles.listBoxItemBase, 'text-sm', {
                  [styles.active]: childActive,
                })}
              >
                <span
                  className={cn(styles.listBoxTitle, 'text-sm', {
                    ['!font-bold']: childActive,
                  })}
                >
                  {child.label}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </li>
  );
};
